import { restoreLocalFile, storeLocalFile } from "@/lib/local-file-storage";

export type SavedArtwork = {
  file: File;
  x: number;
  y: number;
  zoom: number;
};

export type ProductConfigurationDraft = {
  productId: string;
  updatedAt: string;
  selections: Record<string, string>;
  artwork: Record<string, SavedArtwork>;
};

const databaseName = "en-papel-drafts";
const storeName = "product-configurations";
const databaseVersion = 1;

function openDraftDatabase() {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB is unavailable."));
  }

  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(databaseName, databaseVersion);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(storeName, { keyPath: "productId" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("Could not open the draft database."));
  });
}

function runTransaction<T>(
  database: IDBDatabase,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
) {
  return new Promise<T>((resolve, reject) => {
    const transaction = database.transaction(storeName, mode);
    const request = action(transaction.objectStore(storeName));
    let result: T;

    request.onsuccess = () => {
      result = request.result;
    };
    request.onerror = () => {
      reject(request.error ?? new Error("Could not access the draft."));
    };
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("The draft transaction failed."));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("The draft transaction was aborted."));
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseDraft(value: unknown, productId: string): ProductConfigurationDraft {
  if (
    !isRecord(value) ||
    value.productId !== productId ||
    typeof value.updatedAt !== "string" ||
    !isRecord(value.selections) ||
    !isRecord(value.artwork)
  ) {
    throw new Error("The saved product draft is invalid.");
  }

  const selections: Record<string, string> = {};
  for (const [groupId, selectedValueId] of Object.entries(value.selections)) {
    if (typeof selectedValueId !== "string") {
      throw new Error("The saved product options are invalid.");
    }
    selections[groupId] = selectedValueId;
  }

  const artwork: Record<string, SavedArtwork> = {};
  for (const [areaId, entry] of Object.entries(value.artwork)) {
    if (
      !isRecord(entry) ||
      typeof entry.x !== "number" ||
      !Number.isFinite(entry.x) ||
      typeof entry.y !== "number" ||
      !Number.isFinite(entry.y) ||
      typeof entry.zoom !== "number" ||
      !Number.isFinite(entry.zoom)
    ) {
      throw new Error("The saved product artwork is invalid.");
    }
    artwork[areaId] = {
      file: restoreLocalFile(entry.file),
      x: entry.x,
      y: entry.y,
      zoom: entry.zoom,
    };
  }

  return {
    productId,
    updatedAt: value.updatedAt,
    selections,
    artwork,
  };
}

export async function loadProductDraft(productId: string) {
  const database = await openDraftDatabase();
  try {
    const stored = await runTransaction<unknown>(
      database,
      "readonly",
      (store) => store.get(productId),
    );
    return stored === undefined ? null : parseDraft(stored, productId);
  } finally {
    database.close();
  }
}

export async function saveProductDraft(draft: ProductConfigurationDraft) {
  const artworkEntries = await Promise.all(
    Object.entries(draft.artwork).map(async ([areaId, entry]) => [
      areaId,
      {
        ...entry,
        file: await storeLocalFile(entry.file),
      },
    ]),
  );
  const storedDraft = {
    ...draft,
    artwork: Object.fromEntries(artworkEntries),
  };
  const database = await openDraftDatabase();
  try {
    await runTransaction(database, "readwrite", (store) => store.put(storedDraft));
  } finally {
    database.close();
  }
}

export async function deleteProductDraft(productId: string) {
  const database = await openDraftDatabase();
  try {
    await runTransaction(database, "readwrite", (store) => store.delete(productId));
  } finally {
    database.close();
  }
}
